import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface CategoriaFinanceiraType {
    id: number;
    name: string;    
    empresaId: number;
}

export interface CreateCategoriaFinanceiraType {
    name: string;
    empresaId: number;
}

export interface UpdateCategoriaFinanceiraType {
    id: number;
    name: string;
}

export default function useCategoriaFinanceira() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listCategoriaFinanceira, setListCategoriaFinanceira] = useState<CategoriaFinanceiraType[]>([]);
    const [listCategoriaFinanceiraByEmpresa, setListCategoriaFinanceiraByEmpresa] = useState<CategoriaFinanceiraType[]>([]);
    const [categoriaFinanceiraPageCount, setCategoriaFinanceiraPageCount] = useState<number>(0);
    const [initialListCategoriaFinanceira, setInitialListCategoriaFinanceira] = useState<CategoriaFinanceiraType[]>([]);
    const [categoriaFinanceira, setCategoriaFinanceira] = useState<CategoriaFinanceiraType>({
        id: 0,
        name: "",
        empresaId: 0,
    });

    function createCategoriaFinanceira(createCategoriaFinanceiraType: CreateCategoriaFinanceiraType): Promise<CreateCategoriaFinanceiraType | null> {
        setLoading(true);
        return api
            .post(`/CategoriaFinanceira/create`, createCategoriaFinanceiraType)
            .then((response) => {
                const createdCategoriaFinanceira: CreateCategoriaFinanceiraType = response.data;
                showToast("success", "Categoria financeira criada com sucesso.");
                setLoading(false);
                return createdCategoriaFinanceira;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar categoria financeira. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar categoria financeira:", error);
                return null;
            });
    }

    function getAllCategoriaFinanceiraPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<CategoriaFinanceiraType[] | null> {
        setLoading(true);
        let url = `/CategoriaFinanceira/getAllCategoriaFinanceiraPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListCategoriaFinanceira(response.data.result);
                setInitialListCategoriaFinanceira(response.data.result);
                setCategoriaFinanceiraPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar categorias do financeiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar categorias do financeiro:", error);
                return null;
            });
    }

    function updateCategoriaFinanceira(updateCategoriaFinanceiraData: UpdateCategoriaFinanceiraType): Promise<UpdateCategoriaFinanceiraType | null> {
        setLoading(true);
        return api
            .put(`/CategoriaFinanceira/${updateCategoriaFinanceiraData.id}`, updateCategoriaFinanceiraData)
            .then((response) => {
                showToast("success", "Categoria atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar categoria. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar categoria:", error);
                return null;
            });
    }

    function deleteCategoriaFinanceira(categoriaFinanceiraId: number): Promise<CategoriaFinanceiraType | null> {
        setLoading(true);
        return api
            .delete(`/CategoriaFinanceira/${categoriaFinanceiraId}`)
            .then(() => {
                setCategoriaFinanceira({
                    id: 0,
                    name: "",
                    empresaId: 0,
                });
                showToast("success", "Categoria deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar categoria. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar categoria:", error);
                return null;
            });
    }

    function handleGetCategoriaFinanceiraByEmpresaId(empresaId: number): Promise<CategoriaFinanceiraType[] | null> {
            setLoading(true);
            return api
                .get(`/CategoriaFinanceira/getCategoriaFinanceiraByEmpresaId?empresaId=${empresaId}`)
                .then((response) => {
                    const categoriaFinanceira: CategoriaFinanceiraType[] = response.data;
                    setListCategoriaFinanceiraByEmpresa(categoriaFinanceira);
                    setLoading(false);
                    return categoriaFinanceira;
                })
                .catch((error) => {
                    if (error?.response?.status === 401) {
                        handleLogout();
                        showToast(
                            "error",
                            "Sessão desconectada. Favor login novamente.",
                            error
                        );
                    } else {
                        showToast(
                            "error",
                            "Erro ao buscar informações das categorias do financeiro. Verifique novamente mais tarde.",
                            error
                        );
                    }
                    setLoading(false);
                    console.error("Erro ao buscar informações das categorias do financeiro:", error);
                    return null;
                });
        }

    return {
        createCategoriaFinanceira,
        getAllCategoriaFinanceiraPaged,
        updateCategoriaFinanceira,
        deleteCategoriaFinanceira,
        setListCategoriaFinanceira,
        listCategoriaFinanceira,
        setCategoriaFinanceiraPageCount,
        categoriaFinanceiraPageCount,
        setInitialListCategoriaFinanceira,
        initialListCategoriaFinanceira,
        setCategoriaFinanceira,
        categoriaFinanceira,
        handleGetCategoriaFinanceiraByEmpresaId,
        setListCategoriaFinanceiraByEmpresa,
        listCategoriaFinanceiraByEmpresa,
    };
}